'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { Sparkles, Gamepad2, Shield, Users, ArrowLeft } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function AuthPage() {
  const searchParams = useSearchParams();
  const initialMode = searchParams?.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--background)' }}>
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col items-center justify-center p-12 overflow-hidden">
        {/* Orbs */}
        <div
          className="hero-glow-orb w-[500px] h-[500px] -top-40 -left-40 opacity-25"
          style={{ background: 'var(--primary)' }}
        />
        <div
          className="hero-glow-orb w-[400px] h-[400px] -bottom-40 -right-40 opacity-20"
          style={{ background: 'var(--accent)' }}
        />
        <div
          className="hero-glow-orb w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10"
          style={{ background: 'var(--neon-pink)' }}
        />

        {/* Floating emojis */}
        <div className="absolute top-20 left-20 text-5xl animate-float">🎉</div>
        <div className="absolute top-40 right-32 text-4xl animate-float" style={{ animationDelay: '0.8s' }}>🍾</div>
        <div className="absolute bottom-32 left-28 text-4xl animate-float" style={{ animationDelay: '1.6s' }}>🎭</div>
        <div className="absolute bottom-20 right-20 text-5xl animate-float" style={{ animationDelay: '0.4s' }}>🃏</div>

        <div className="relative z-10 text-center max-w-lg">
          <div className="flex items-center justify-center gap-3 mb-8">
            <AppLogo size={52} />
            <span className="font-extrabold text-3xl text-gradient-primary">PartyGames</span>
          </div>

          <h1 className="font-extrabold text-5xl leading-tight mb-6">
            <span className="text-gradient-party">Die Party</span>
            <br />
            <span style={{ color: 'var(--foreground)' }}>beginnt hier.</span>
          </h1>

          <p className="text-lg mb-10 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Melde dich an, um deine Spieler zu speichern, Premium freizuschalten und überall weiterzuspielen.
          </p>

          {/* Feature highlights */}
          <div className="space-y-4">
            {[
              { icon: Gamepad2, label: '14+ Party-Spiele sofort verfügbar', color: 'var(--neon-violet)' },
              { icon: Users, label: 'Spieler einmal anlegen, überall nutzen', color: 'var(--neon-cyan)' },
              { icon: Sparkles, label: 'Premium: Spicy & Chaos Kategorien', color: 'var(--neon-amber)' },
              { icon: Shield, label: 'Sicher & DSGVO-konform', color: 'var(--neon-green)' },
            ]?.map((item) => {
              const Icon = item?.icon;
              return (
                <div key={`feature-${item?.label}`} className="flex items-center gap-3 text-left">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${item?.color}20` }}
                  >
                    <Icon size={16} style={{ color: item?.color }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                    {item?.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Right Panel — Form */}
      <div
        className="w-full lg:w-1/2 xl:w-2/5 flex flex-col items-center justify-center p-6 sm:p-10 relative"
        style={{ background: 'var(--secondary)', borderLeft: '1px solid var(--border)' }}
      >
        {/* Back to Home Button */}
        <div className="absolute top-5 left-5">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl transition-all hover:bg-white/5"
            style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
          >
            <ArrowLeft size={15} />
            <span>Startseite</span>
          </Link>
        </div>

        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={36} />
          <span className="font-extrabold text-xl text-gradient-primary">PartyGames</span>
        </div>

        <div className="w-full max-w-sm">
          {/* Mode Toggle */}
          <div
            className="flex rounded-xl p-1 mb-8"
            style={{ background: 'var(--muted)' }}
          >
            <button
              onClick={() => setMode('login')}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
              style={{
                background: mode === 'login' ? 'var(--card)' : 'transparent',
                color: mode === 'login' ? 'var(--foreground)' : 'var(--muted-foreground)',
                boxShadow: mode === 'login' ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              Anmelden
            </button>
            <button
              onClick={() => setMode('register')}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
              style={{
                background: mode === 'register' ? 'var(--card)' : 'transparent',
                color: mode === 'register' ? 'var(--foreground)' : 'var(--muted-foreground)',
                boxShadow: mode === 'register' ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              Registrieren
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToRegister={() => setMode('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setMode('login')} />
          )}

          {/* Footer links */}
          <p className="text-center text-xs mt-8" style={{ color: 'var(--muted-foreground)' }}>
            <Link href="/impressum" className="underline hover:opacity-80">Impressum</Link>
            {' · '}
            <Link href="/impressum#datenschutz" className="underline hover:opacity-80">Datenschutz</Link>
            {' · '}
            <Link href="/impressum#agb" className="underline hover:opacity-80">AGB</Link>
          </p>
        </div>
      </div>
    </div>
  );
}